"use client";

import { useState } from "react";
import { Puzzle } from "lucide-react";

import { readSkillError } from "@/components/command-skills/skill-api";
import { SkillDetailView } from "@/components/command-skills/skill-detail-view";
import { pickSkillForAgent } from "@/components/command-skills/skill-utils";
import type { SkillChipVariant } from "@/components/command-skills/skill-chat";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useSkillPages, flattenSkillPages } from "@/hooks/use-command-skills";
import { cn } from "@/lib/utils";

interface SkillChatChipProps {
  name: string;
  variant: SkillChipVariant;
  className?: string;
}

export function SkillChatChip({ name, variant, className }: SkillChatChipProps) {
  const [open, setOpen] = useState(false);
  const label = variant === "preparing" ? "Preparing skill" : "Using skill";

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
        className={cn(
          "my-1.5 inline-flex max-w-full items-center gap-1.5 rounded-lg border border-border/80 bg-muted/50 px-2.5 py-1 text-[13px] text-foreground transition-colors hover:bg-muted",
          className,
        )}
      >
        <Puzzle className="size-3.5 shrink-0 text-primary" />
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground">·</span>
        <span className="truncate font-mono text-xs">{name}</span>
      </button>
      {open ? (
        <SkillPreviewSheet skillName={name} open={open} onOpenChange={setOpen} />
      ) : null}
    </>
  );
}

function SkillPreviewSheet({
  skillName,
  open,
  onOpenChange,
}: {
  skillName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const query = useSkillPages({ search: skillName, enabled: open });
  const match = pickSkillForAgent(flattenSkillPages(query.data), skillName);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        overlayClassName="bg-black/40"
        className="flex h-full w-full flex-col gap-0 overflow-hidden border-l bg-background p-0 shadow-2xl sm:max-w-[920px] [&>button]:right-4 [&>button]:top-[26px] [&>button]:flex [&>button]:size-8 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-lg [&>button]:border [&>button]:border-border [&>button]:bg-background [&>button]:opacity-100 [&>button]:shadow-none"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{skillName}</SheetTitle>
          <SheetDescription>Skill details</SheetDescription>
        </SheetHeader>
        {query.isLoading ? (
          <p className="px-6 py-10 text-sm text-muted-foreground">Loading skill…</p>
        ) : query.error ? (
          <div className="px-6 py-10">
            <p className="text-sm text-destructive">{readSkillError(query.error).message}</p>
            <Button type="button" variant="outline" className="mt-4" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : match ? (
          <SkillDetailView skillId={match.id} embedded onBack={() => onOpenChange(false)} />
        ) : (
          <div className="px-6 py-10">
            <p className="text-sm text-muted-foreground">
              {skillName} is not in your skills list.
            </p>
            <Button type="button" variant="outline" className="mt-4" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
